// Which sale a job keeps (BL-139): a job whose sales ServiceTitan deleted, with different totals, shows
// both side by side (when each was made and sold, its amount and lines, who sold it, when ServiceTitan
// last changed it) and the office keeps one, both, or neither (migration.reviewKeepSale). The answer
// shows with who gave it and when, and it can change until go-live; the import follows it on its next
// load. Only those who resolve review items answer; everyone who sees the place sees the sales.

import { useState } from 'react';
import { client } from '../api.ts';
import { errorText } from '../books/ui.tsx';
import { money } from '../books/model.ts';
import {
  KEEP_BOTH,
  KEEP_NEITHER,
  keepText,
  keptToast,
  saleDay,
  saleLabel,
  saleLineText,
  sellerText,
  type SalePair,
} from './model.ts';

export function KeepSale({
  id,
  pair,
  may,
  timeZone,
  toast,
  onDone,
}: {
  /** the review item */
  id: string;
  pair: SalePair;
  /** holds migration.operate */
  may: boolean;
  timeZone: string;
  toast: (m: string) => void;
  onDone: () => Promise<void> | void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const answered = keepText(pair);
  const asking = may && pair.changeable;
  const keep = async (choice: string) => {
    setBusy(choice);
    try {
      const r = await client.call<{ changed: boolean; keep: string }>('migration.reviewKeepSale', {
        id,
        keep: choice,
      });
      toast(keptToast(pair, choice, r.changed));
      await onDone();
    } catch (e) {
      toast(errorText(e));
    } finally {
      setBusy(null);
    }
  };
  const choice = (value: string, words: string) => (
    <button
      type="button"
      className="btn"
      aria-pressed={pair.keep === value}
      disabled={busy !== null || pair.keep === value}
      onClick={() => void keep(value)}
    >
      {busy === value ? 'Saving…' : words}
    </button>
  );
  return (
    <div className="mig-keep-sale">
      <div className="mig-sales" role="group" aria-label={`The sales on job ${pair.job}`}>
        {pair.sales.map((s) => (
          <section
            key={s.stId}
            className={`mig-sale${pair.keep === s.stId ? ' kept' : ''}`}
            aria-label={saleLabel(pair, s.stId)}
          >
            <b className="mig-sale-name">
              {saleLabel(pair, s.stId)}
              <span className="muted small block">
                {s.title ? `${s.title} · ` : ''}ServiceTitan estimate {s.stId}
              </span>
            </b>
            <dl className="mig-sale-facts">
              <dt>Made</dt>
              <dd>{saleDay(s.madeOn, timeZone)}</dd>
              <dt>Sold</dt>
              <dd>{saleDay(s.soldOn, timeZone)}</dd>
              <dt>Amount</dt>
              <dd>{s.subtotalCents === null ? 'Not recorded' : `${money(s.subtotalCents)} before tax`}</dd>
              <dt>Sold by</dt>
              <dd>{sellerText(s)}</dd>
              <dt>Deleted in ServiceTitan</dt>
              <dd>
                {s.changedOn ? `Last changed ${saleDay(s.changedOn, timeZone)}. ` : ''}
                ServiceTitan doesn’t say why.
              </dd>
            </dl>
            {s.lines.length > 0 ? (
              <ul className="mig-sale-lines">
                {s.lines.map((l, i) => (
                  <li key={i}>
                    <span>{saleLineText(l)}</span>
                    <span className="num">{money(l.totalCents)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted small">No lines recorded.</p>
            )}
            <p className="muted small">{s.keptHere ? 'On the job now.' : 'Off the job now.'}</p>
            {asking && choice(s.stId, 'Keep this one')}
          </section>
        ))}
      </div>
      {answered && (
        <p className="small mig-sale-answer">
          {answered}{' '}
          <span className="muted">
            {[pair.answeredBy, pair.answeredAt ? saleDay(pair.answeredAt, timeZone) : null]
              .filter(Boolean)
              .join(', ')}
          </span>
        </p>
      )}
      {asking ? (
        <div className="mig-choice" role="group" aria-label="Or">
          {choice(KEEP_BOTH, 'Keep both')}
          {choice(KEEP_NEITHER, 'Keep neither')}
        </div>
      ) : (
        !pair.changeable && <p className="muted small">The company has gone live: this answer stands.</p>
      )}
    </div>
  );
}
